import { Routes } from '@angular/router';
import { AuthRoutes } from '@core/routes/auth.routes';
import { LayoutDashboard } from '@shared/components/layouts/layout-dashboard/layout-dashboard';
import { notAuthenticatedGuard } from '@features/auth/guards/not-authenticated.guard';
import { CompanyRoutes } from '@core/routes/company.routes';
import { VehiclesRoutes } from '@core/routes/vehicle.routes';
import { ReportsRoutes } from '@core/routes/reports.routes';
import { AdvancedMaintenanceRoutes } from '@core/routes/advanced-maintenance.routes';
import { ConfigurationRoutes } from '@core/routes/configuration.routes';
import { advanceMaintenanceModalityGuard } from '@features/configuration/guards/advance-maintenance-modality.guard';
import { advancedMaintenanceEnabledGuard } from '@features/configuration/guards/advanced-maintenance-enabled.guard';
import { permissionGuard } from '@core/guards/permission.guard';
import { publicRouteGuard } from '@features/auth/guards/public-route.guard';

export const routes: Routes = [
  { path: '', redirectTo: 'auth', pathMatch: 'full' },
  ...AuthRoutes,
  {
    path: '',
    component: LayoutDashboard,
    canActivateChild: [notAuthenticatedGuard, permissionGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        title: 'Inicio',
        data: { icon: 'home', permission: 'home.view' },
        loadComponent: () => import('@features/home/home'),
      },
      ...ConfigurationRoutes,
      ...CompanyRoutes,
      {
        path: 'alerts',
        pathMatch: 'full',
        redirectTo: 'reports/checklists/alerts',
      },
      {
        path: 'drivers',
        title: 'Conductores',
        data: { icon: 'id_card', permission: 'drivers.view' },
        children: [
          {
            path: '',
            title: 'Gestionar conductores',
            loadComponent: () =>
              import('@features/drivers/drivers.component').then((m) => m.DriversComponent),
          },
          {
            path: ':driverId/licenses',
            title: 'Licencias asociadas al conductor',
            data: { backFallback: '/drivers' },
            loadComponent: () =>
              import('@features/drivers/view/license/licence.component').then(
                (m) => m.LicenceComponent,
              ),
          },
          {
            path: ':driverId/files',
            title: 'Archivos asociados al conductor',
            data: { backFallback: '/drivers' },
            loadComponent: () =>
              import('@features/drivers/view/files/files.component').then((m) => m.FilesComponent),
          },
        ],
      },
      ...VehiclesRoutes,
      {
        path: 'fuel',
        title: 'Consumo de combustible',
        data: { icon: 'local_gas_station', permission: 'fuel.view' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/fuel/fuel.component').then((m) => m.FuelComponent),
          },
        ],
      },
      {
        path: 'basic-maintenance',
        title: 'Mantenimiento básico',
        canActivate: [advanceMaintenanceModalityGuard],
        data: {
          icon: 'build',
          headerTitle: 'Mantenimiento básico de vehículos',
          requiresAdvancedMaintenance: false,
          permission: 'basicMaintenance.view',
        },
        loadComponent: () =>
          import('@features/basic-maintenance/basic-mantenance.component').then(
            (m) => m.BasicMantenanceComponent,
          ),
      },
      {
        path: 'check-list',
        title: 'Listas de chequeo',
        data: { icon: 'fact_check', permission: 'checkList.view' },
        loadComponent: () =>
          import('@features/check-list/check-list.component').then((m) => m.CheckListComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'my-lists' },
          {
            path: 'classifications',
            title: 'Clasificación de ítems',
            // data: { backFallback: '/check-list/my-lists' },
            loadComponent: () =>
              import('@features/check-list/views/classifications/classifications.component').then(
                (m) => m.ClassificationsComponent,
              ),
          },
          {
            path: 'my-lists',
            title: 'Mis listas de chequeo',
            loadComponent: () =>
              import('@features/check-list/views/my-checklists/my-checklists.component').then(
                (m) => m.MyChecklistsComponent,
              ),
          },
          {
            path: 'new',
            title: 'Crear lista de chequeo',
            data: { backFallback: '/check-list/my-lists', permission: 'checkList.manage' },
            loadComponent: () =>
              import('@features/check-list/views/check-list-form/check-list-form.component').then(
                (m) => m.CheckListFormComponent,
              ),
          },
          {
            path: ':id/edit',
            title: 'Editar lista de chequeo',
            data: { backFallback: '/check-list/my-lists', permission: 'checkList.manage' },
            loadComponent: () =>
              import('@features/check-list/views/check-list-form/check-list-form.component').then(
                (m) => m.CheckListFormComponent,
              ),
          },
        ],
      },
      ...AdvancedMaintenanceRoutes,
      ...ReportsRoutes,
      {
        path: 'power-bi',
        title: 'Informes BI',
        canActivate: [advancedMaintenanceEnabledGuard],
        data: { icon: 'insights', redirectWhenDisabled: '/home' },
        loadComponent: () => import('@features/power-bi/power-bi-report.component'),
      },
      {
        // Dentro del layout para que el usuario conserve el menú al ser rechazado.
        path: 'not-authorized',
        title: 'Acceso restringido',
        data: { hideInMenu: true },
        loadComponent: () => import('@features/not-authorized/not-authorized'),
      },
    ],
  },
  {
    path: 'service-unavailable',
    canActivate: [publicRouteGuard],
    loadComponent: () => import('@features/service-unavailable/service-unavailable'),
  },
  {
    path: 'not-found',
    canActivate: [publicRouteGuard],
    loadComponent: () => import('@features/not-found/not-found'),
  },
  { path: '**', redirectTo: 'not-found' },
];
